import React from 'react';
import { Globe, Shield, Key, Save, Loader2 } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';

interface ProjectIngressCardProps {
  canAdmin: boolean;
  origins: string;
  setOrigins: (origins: string) => void;
  redirects: string;
  setRedirects: (redirects: string) => void;
  rateLimit: number;
  setRateLimit: (limit: number) => void;
  isSavingIngress: boolean;
  handleSaveIngress: (e: React.FormEvent) => void;
}

export const ProjectIngressCard: React.FC<ProjectIngressCardProps> = ({
  canAdmin,
  origins,
  setOrigins,
  redirects,
  setRedirects,
  rateLimit,
  setRateLimit,
  isSavingIngress,
  handleSaveIngress,
}) => {
  return (
    <form onSubmit={handleSaveIngress} className="space-y-5">
      {/* CORS & Origin Verification */}
      <Card className="p-5 bg-cf-surface border-cf-border space-y-4 shadow-xs">
        <div className="flex items-center gap-2 text-sm font-semibold text-cf-textPrimary">
          <Globe size={16} className="text-cf-orange" />
          <span>Allowed CORS Origins</span>
        </div>
        <p className="text-xs text-cf-textSecondary">
          Origins permitted to submit via standard browser fetch/XHR form posts to this project. Requests from unauthorized origins receive HTTP 403 Forbidden.
        </p>
        <Input
          size="xs"
          disabled={!canAdmin}
          value={origins}
          onChange={(e) => setOrigins(e.target.value)}
          placeholder="e.g. https://mycompany.com, https://app.mycompany.com"
        />
      </Card>

      {/* Redirect URLs Boundaries */}
      <Card className="p-5 bg-cf-surface border-cf-border space-y-4 shadow-xs">
        <div className="flex items-center gap-2 text-sm font-semibold text-cf-textPrimary">
          <Shield size={16} className="text-cf-orange" />
          <span>Open Redirect Whitelist</span>
        </div>
        <p className="text-xs text-cf-textSecondary">
          Whitelist for <code>_redirect</code> / <code>_next</code> target URLs to prevent open-redirect phishing attacks.
        </p>
        <Input
          size="xs"
          disabled={!canAdmin}
          value={redirects}
          onChange={(e) => setRedirects(e.target.value)}
          placeholder="e.g. https://mycompany.com/thank-you"
        />
      </Card>

      {/* Ingestion Rate Limiter */}
      <Card className="p-5 bg-cf-surface border-cf-border space-y-4 shadow-xs">
        <div className="flex items-center gap-2 text-sm font-semibold text-cf-textPrimary">
          <Key size={16} className="text-cf-orange" />
          <span>Ingestion Rate Limiting</span>
        </div>
        <p className="text-xs text-cf-textSecondary">
          Maximum incoming form and webhook requests per minute before returning HTTP 429 Too Many Requests.
        </p>
        <div className="max-w-xs">
          <Input
            type="number"
            size="xs"
            disabled={!canAdmin}
            value={rateLimit}
            onChange={(e) => setRateLimit(Number(e.target.value))}
          />
        </div>
      </Card>

      {canAdmin && (
        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            size="xs"
            disabled={isSavingIngress}
            leftIcon={isSavingIngress ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          >
            {isSavingIngress ? 'Saving...' : 'Save Ingress & Security'}
          </Button>
        </div>
      )}
    </form>
  );
};
